import { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import { Sparkles, X, Send, Bot, Clock, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getSessionId = () => {
  let sid = localStorage.getItem('masala-spin-bot-session');
  if (!sid) {
    sid = `bot-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem('masala-spin-bot-session', sid);
  }
  return sid;
};

export const RecipeBot = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [turns, setTurns] = useState([]); // [{role, content}]
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [turns, loading]);

  const ask = useCallback(async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setTurns((t) => [...t, { role: 'user', content: userMsg }]);
    setInput('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/bot/recipe-suggest`, {
        session_id: getSessionId(),
        ingredients: userMsg,
      });
      setTurns((t) => [...t, { role: 'bot', content: data }]);
    } catch (e) {
      setTurns((t) => [...t, { role: 'bot', content: { error: e.response?.data?.detail || 'Bot unavailable — try again soon.' } }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading]);

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      ask();
    }
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="open-recipe-bot-button"
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-5 py-4 rounded-full bg-gradient-to-r from-[#F97316] to-[#FBBF24] text-white font-['Outfit'] font-semibold shadow-2xl hover:shadow-[0_20px_60px_rgba(249,115,22,0.5)] hover:-translate-y-1 transition-all duration-300"
        >
          <Sparkles className="w-5 h-5" />
          <span className="hidden sm:inline">Ask the Chef</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="recipe-bot-panel"
          className="fixed bottom-6 right-6 z-50 w-[min(420px,calc(100vw-3rem))] h-[min(620px,calc(100vh-6rem))] bg-white rounded-3xl shadow-2xl border border-[#1C1917]/10 flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-[#F97316] to-[#FBBF24] text-white">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <p className="font-['Playfair_Display'] text-lg font-bold leading-none">Chef Bot</p>
                <p className="text-xs text-white/80 font-['Outfit'] mt-0.5">What's in your kitchen?</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="close-recipe-bot-button" aria-label="Close chef bot" className="p-2 rounded-full hover:bg-white/10">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4 bg-[#FDFBF7]">
            {turns.length === 0 && (
              <div className="text-center py-6">
                <Sparkles className="w-8 h-8 text-[#F97316] mx-auto mb-3" />
                <p className="text-sm text-[#1C1917]/70 font-['Outfit'] leading-relaxed">
                  Tell me what ingredients you have — e.g.<br />
                  <span className="italic">"potato, onion, cumin, tomato"</span><br />
                  and I'll suggest quick oil-free Indian recipes.
                </p>
              </div>
            )}

            {turns.map((t, i) => (
              <div key={`turn-${i}-${t.role}`}>
                {t.role === 'user' && (
                  <div className="flex justify-end">
                    <div className="max-w-[80%] px-4 py-3 bg-[#2D4A22] text-white rounded-2xl rounded-br-sm text-sm font-['Outfit']">
                      {t.content}
                    </div>
                  </div>
                )}
                {t.role === 'bot' && (
                  <div className="space-y-3">
                    {t.content.error && (
                      <div className="px-4 py-3 bg-red-50 text-red-700 rounded-2xl text-sm font-['Outfit'] flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        {t.content.error}
                      </div>
                    )}
                    {t.content.recipes?.map((r, idx) => (
                      <div key={`${r.name}-${idx}`} className="bg-white rounded-2xl p-4 border border-[#1C1917]/5 shadow-sm font-['Outfit']">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h5 className="font-['Playfair_Display'] text-base font-bold text-[#1C1917] leading-tight">{r.name}</h5>
                          {r.time && (
                            <span className="flex-shrink-0 flex items-center gap-1 text-xs text-[#F97316]">
                              <Clock className="w-3 h-3" />{r.time}
                            </span>
                          )}
                        </div>
                        {r.uses?.length > 0 && (
                          <p className="text-xs text-[#2D4A22] mb-1 flex items-start gap-1">
                            <CheckCircle2 className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            <span>Uses: {r.uses.join(', ')}</span>
                          </p>
                        )}
                        {r.missing?.length > 0 && (
                          <p className="text-xs text-[#1C1917]/60 mb-2">
                            + need: {r.missing.join(', ')}
                          </p>
                        )}
                        <ol className="text-xs text-[#1C1917]/80 space-y-1 list-decimal pl-4 mt-2">
                          {(r.steps || []).map((s, j) => <li key={`${s.slice(0, 12)}-${j}`}>{s}</li>)}
                        </ol>
                      </div>
                    ))}
                    {t.content.note && (
                      <p className="text-xs text-[#1C1917]/60 italic font-['Outfit'] px-2">— {t.content.note}</p>
                    )}
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="flex items-center gap-2 text-sm text-[#1C1917]/60 font-['Outfit']">
                <div className="flex gap-1">
                  <span className="w-2 h-2 rounded-full bg-[#F97316] animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 rounded-full bg-[#F97316] animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 rounded-full bg-[#F97316] animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                Chef Bot is thinking…
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-3 border-t border-[#1C1917]/5 bg-white">
            <div className="flex gap-2 items-end">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKey}
                placeholder="Ingredients you have…"
                data-testid="recipe-bot-input"
                rows={1}
                className="resize-none min-h-[44px] max-h-24 font-['Outfit'] text-sm border-[#1C1917]/10 focus:border-[#F97316]"
              />
              <Button
                onClick={ask}
                disabled={loading || !input.trim()}
                data-testid="recipe-bot-send"
                className="bg-[#F97316] hover:bg-[#EA580C] text-white rounded-full w-11 h-11 p-0 flex-shrink-0"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
